import React, {useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Box, Grid, Skeleton, Text, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useHeaderSize, useHeaderSizeWithoutBanner, useViewport} from '_customHooks'
import type {StoriesPageContent} from '_utils'
import StoriesNav from './partials/stories-nav'
import StorySection from './partials/story-section'

// Path of the Stories overview; section slugs are appended to it (ex. /stories/campagne).
export const STORIES_BASE_PATH = '/stories'

export interface StoriesProps {
    content: StoriesPageContent
    /** Slug of the section to expand. When omitted the overview is rendered. */
    activeSectionId?: string
    /** Whether the Page Designer content is still loading. */
    isLoading?: boolean
}

/**
 * @name Stories
 *
 * @component
 *
 * @description
 * Renders the Stories page: a sticky sidebar holding the page title, the section navigation
 * and the intro text, next to the content column. The content column shows either the overview
 * (the "Highlights" block followed by every section in its collapsed layout) or a single
 * expanded section when a slug is active.
 *
 * @param {StoriesProps} props - The properties object.
 * @param {StoriesPageContent} props.content - The mapped Stories page content.
 * @param {string} [props.activeSectionId] - The section to expand.
 *
 * @returns {JSX.Element} The rendered `Stories` component.
 */
const Stories: React.FC<StoriesProps> = ({content, activeSectionId, isLoading = false}) => {
    const {formatMessage} = useIntl()
    const headerSize = useHeaderSizeWithoutBanner()
    const {headerSize: headerSizeWithBanner} = useHeaderSize()
    const styles = useMultiStyleConfig('Stories', {headerSize, headerSizeWithBanner})
    const {buildUrl} = useMultiSite()
    const {isDesktop} = useViewport()

    const basePath = buildUrl(STORIES_BASE_PATH)
    const {title, description, sections, highlights} = content

    const activeSection = activeSectionId ? sections.find((section) => section.sectionId === activeSectionId) : undefined

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [activeSectionId])

    if (isLoading) {
        return (
            <Box sx={styles.wrapper} data-testid="stories-skeleton" aria-busy="true" role="status" aria-label="Loading Stories">
                <Box as="aside" sx={styles.sidebar}>
                    <VStack display={{base: 'none', lg: 'flex'}} align="stretch" spacing="spacing.6" data-testid="stories-skeleton-desktop-nav">
                        <Skeleton h="32px" w="75%" />
                        <VStack as="nav" sx={styles.nav} align="stretch">
                            {['70%', '55%', '65%', '45%'].map((width, index) => (
                                <Skeleton key={`stories-nav-skeleton-${index}`} h="16px" w={width} />
                            ))}
                        </VStack>
                        <Skeleton h="64px" w="100%" />
                    </VStack>
                    <VStack display={{base: 'flex', lg: 'none'}} align="stretch" spacing="spacing.4" data-testid="stories-skeleton-mobile-nav">
                        <Skeleton h="32px" w="60%" />
                        <Skeleton h="16px" w="80%" />
                        <Skeleton h="48px" w="100%" />
                    </VStack>
                </Box>
                <Box sx={styles.content}>
                    <Box as="section" sx={styles.section}>
                        <Skeleton h={{base: '24px', lg: '32px'}} w="55%" />
                        <Skeleton h="16px" w="70%" />
                        <Grid sx={styles.grid} templateColumns={{base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))'}}>
                            {Array.from({length: 6}, (_, index) => (
                                <Box key={`stories-card-skeleton-${index}`} display={{base: index < 4 ? 'block' : 'none', md: 'block'}}>
                                    <Skeleton w="100%" aspectRatio={0.75} />
                                    <Skeleton mt="spacing.2" h="16px" w="75%" />
                                    <Skeleton mt="spacing.1" h="14px" w="50%" />
                                </Box>
                            ))}
                        </Grid>
                    </Box>
                    <Box as="section" sx={styles.section}>
                        <Skeleton h={{base: '24px', lg: '32px'}} w="40%" />
                        <Grid sx={styles.grid} templateColumns={{base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))'}}>
                            {Array.from({length: 3}, (_, index) => (
                                <Box key={`stories-secondary-skeleton-${index}`} display={{base: index < 2 ? 'block' : 'none', md: 'block'}}>
                                    <Skeleton w="100%" aspectRatio={0.75} />
                                    <Skeleton mt="spacing.2" h="16px" w="65%" />
                                </Box>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={styles.wrapper} data-testid="stories">
            <StoriesNav
                title={title}
                description={description}
                sections={sections}
                basePath={basePath}
                activeSectionId={activeSectionId}
                activeSection={activeSection}
            />
            {activeSection && (
                <VStack display={{base: 'flex', lg: 'none'}} align="stretch" spacing="spacing.4" data-testid="stories-mobile-active-section-heading">
                    <Text as="h1" sx={styles.subsectionTitle}>
                        {activeSection.title ?? activeSection.name}
                    </Text>
                    {activeSection.subtitle && <Text dangerouslySetInnerHTML={{__html: activeSection.subtitle}} sx={styles.mobileDescription}></Text>}
                </VStack>
            )}

            {/* Keyed on the active section so the content column replays its fade-in on every navigation. */}
            <Box key={activeSectionId ?? 'stories-overview'} sx={styles.content}>
                {activeSection ? (
                    <StorySection section={activeSection} basePath={basePath} isExpanded hasHeading={isDesktop} isFirstSection />
                ) : (
                    <>
                        {highlights.length > 0 && (
                            <Box as="section" sx={styles.section} data-testid="stories-highlights">
                                <Text as="h2" sx={styles.sectionTitle}>
                                    {formatMessage({id: 'stories.section.highlights', defaultMessage: 'Highlights'})}
                                </Text>
                                {highlights.map((section, index) => (
                                    <StorySection
                                        key={section.id ?? `highlighted-section-${index}`}
                                        section={section}
                                        basePath={basePath}
                                        isFirstSection={index === 0}
                                    />
                                ))}
                            </Box>
                        )}
                        {sections.map((section, index) => (
                            <StorySection
                                key={section.id ?? `stories-section-${index}`}
                                section={section}
                                basePath={basePath}
                                isFirstSection={highlights.length === 0 && index === 0}
                            />
                        ))}
                    </>
                )}
            </Box>
        </Box>
    )
}

export default Stories
